
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent, AvailableTool } from '../../../lib/database';
import AIPromptAssistant from '../../AgentCreation/components/AIPromptAssistant';

interface QuickParametersSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  agentTools: (any & { available_tool?: AvailableTool })[];
  onSave: (params: { instruction: string; temperature: number; max_tokens: number }) => Promise<void>;
  isSaving: boolean;
}

const QuickParametersSidebar: React.FC<QuickParametersSidebarProps> = ({
  isOpen,
  onClose,
  agent,
  agentTools,
  onSave,
  isSaving,
}) => {
  const navigate = useNavigate();
  const [tempInstructions, setTempInstructions] = useState(agent.instruction || '');
  const [tempTemperature, setTempTemperature] = useState(agent.temperature || 0.7);
  const [tempMaxTokens, setTempMaxTokens] = useState(agent.max_tokens || 2000);
  const [showAdvancedParams, setShowAdvancedParams] = useState(false);
  const [showPromptPopout, setShowPromptPopout] = useState(false);

  useEffect(() => {
    setTempInstructions(agent.instruction || '');
    setTempTemperature(agent.temperature || 0.7);
    setTempMaxTokens(agent.max_tokens || 2000);
  }, [agent]);

  const handleSaveChanges = () => {
    onSave({
      instruction: tempInstructions,
      temperature: tempTemperature,
      max_tokens: tempMaxTokens,
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full lg:w-80 bg-white border-l border-gray-200 shadow-lg z-50 lg:z-30 flex flex-col">
        {/* Sidebar Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Quick Parameters</h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">Test different settings without editing the agent</p>
        </div>

        {/* Parameters Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Instructions
              </label>
              <button
                onClick={() => setShowPromptPopout(true)}
                className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
              >
                Pop out
              </button>
            </div>
            <AIPromptAssistant
              value={tempInstructions}
              onChange={(value) => setTempInstructions(value)}
              placeholder="Enter temporary instructions for testing..."
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
            />
            <div className="text-xs text-gray-500 mt-1">
              {tempInstructions.length} characters
            </div>
          </div>

          {/* Tools Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tools ({agentTools.length})
            </label>
            <div className="space-y-2">
              {agentTools.length > 0 ? (
                agentTools.map((tool) => (
                  <div key={tool.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {tool.available_tools.name}
                      </div>
                      {tool.available_tools.description && (
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {tool.available_tools.description}
                        </div>
                      )}
                      <button
                        onClick={() => navigate(`/edit/${agent.id}?tab=tools`)}
                        className="text-xs text-blue-600 hover:text-blue-700 transition-colors mt-1 inline-block"
                      >
                        Configure
                      </button>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={true}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  No tools configured
                </div>
              )}
            </div>
          </div>

          {/* Advanced Parameters */}
          <div>
            <button
              onClick={() => setShowAdvancedParams(!showAdvancedParams)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-gray-700">Advanced Parameters</span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showAdvancedParams ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showAdvancedParams && (
              <div className="mt-4 space-y-4">
                {/* Temperature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature: {tempTemperature}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={tempTemperature}
                    onChange={(e) => setTempTemperature(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Precise (0)</span>
                    <span>Balanced (1)</span>
                    <span>Creative (2)</span>
                  </div>
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Tokens
                  </label>
                  <select
                    value={tempMaxTokens}
                    onChange={(e) => setTempMaxTokens(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    {/* Show current value if not in predefined options */}
                    {![1000, 2000, 4000, 8000].includes(tempMaxTokens) && (
                      <option value={tempMaxTokens}>Current ({tempMaxTokens.toLocaleString()} tokens)</option>
                    )}
                    <option value={1000}>Short (1,000 tokens)</option>
                    <option value={2000}>Medium (2,000 tokens)</option>
                    <option value={4000}>Long (4,000 tokens)</option>
                    <option value={8000}>Very Long (8,000 tokens)</option>
                  </select>
                </div>

                {/* Model Override */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model Override
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                    <option value="">Use Agent Default ({agent.model})</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="claude-3-haiku">Claude 3 Haiku</option>
                    <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-6 border-t border-gray-200">
            <button 
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="w-full px-4 py-3 bg-teal-700 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save & Apply Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Prompt Pop-out Modal */}
      {showPromptPopout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Instructions</h3>
                <button
                  onClick={() => setShowPromptPopout(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 p-6">
              <AIPromptAssistant
                value={tempInstructions}
                onChange={(value) => setTempInstructions(value)}
                placeholder="Enter detailed instructions for your agent..."
                className="w-full h-full min-h-[400px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              />
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {tempInstructions.length} characters
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowPromptPopout(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowPromptPopout(false);
                    // Note: We don't save here, the main button in the sidebar does.
                  }}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickParametersSidebar;
